<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessageBox } from 'element-plus';
import { useAuthStore } from '@/stores/auth';
import { useStoreContextStore } from '@/stores/store-context';
import { hasAny } from '@/utils/permissions';
import type { RoleCode } from '@/types';

interface MenuItem {
  path: string;
  title: string;
  icon: string;
  roles?: RoleCode[];
  group?: string;
}

const auth = useAuthStore();
const ctx = useStoreContextStore();
const router = useRouter();
const route = useRoute();

const menus: MenuItem[] = [
  { path: '/dashboard', title: '数据看板', icon: 'DataLine', roles: ['store_admin'], group: '日常运营' },
  { path: '/cashier', title: '收银', icon: 'Wallet', roles: ['store_admin', 'store_cashier'], group: '日常运营' },
  { path: '/cashier/receipts', title: '收银流水', icon: 'List', roles: ['store_admin', 'store_cashier'], group: '日常运营' },
  { path: '/customers', title: '客户档案', icon: 'User', group: '日常运营' },
  { path: '/consume', title: '消费登记', icon: 'Stamp', roles: ['store_admin', 'store_cashier'], group: '日常运营' },
  { path: '/detection', title: '检测中心', icon: 'Camera', group: '日常运营' },
  { path: '/refund', title: '套餐退款', icon: 'RefreshLeft', roles: ['store_admin'], group: '日常运营' },
  { path: '/swap', title: '套餐置换', icon: 'Switch', roles: ['store_admin'], group: '日常运营' },

  { path: '/inventory', title: '库存台账', icon: 'Box', group: '库存管理' },
  { path: '/inventory/movements', title: '库存流水', icon: 'TrendCharts', group: '库存管理' },
  { path: '/inventory/stocktake', title: '盘点', icon: 'Operation', roles: ['store_admin'], group: '库存管理' },
  { path: '/inventory/low-stock', title: '低库存预警', icon: 'WarnTriangleFilled', group: '库存管理' },
  { path: '/replenish', title: '补货下单', icon: 'ShoppingCart', roles: ['store_admin'], group: '库存管理' },
  { path: '/replenish/orders', title: '补货订单', icon: 'Tickets', roles: ['store_admin'], group: '库存管理' },
  { path: '/b-account', title: 'B 端账户', icon: 'CreditCard', roles: ['store_admin'], group: '库存管理' },

  { path: '/devices', title: '设备状态', icon: 'Monitor', group: '设备运维' },
  { path: '/devices/faults', title: '故障工单', icon: 'Tools', group: '设备运维' },

  { path: '/technicians', title: '技师管理', icon: 'Avatar', roles: ['store_admin'], group: '运营进阶' },
  { path: '/technician-stats', title: '技师KPI', icon: 'Histogram', roles: ['store_admin'], group: '运营进阶' },
  { path: '/operators', title: '员工账号', icon: 'Key', roles: ['store_admin'], group: '运营进阶' },
  { path: '/promotions', title: '促销申请', icon: 'Discount', roles: ['store_admin'], group: '运营进阶' },
  { path: '/settlement', title: '跨店结算', icon: 'Coin', roles: ['store_admin'], group: '运营进阶' },
  { path: '/schedule', title: '技师排班', icon: 'Calendar', roles: ['store_admin'], group: '运营进阶' },
  { path: '/appointments', title: '预约管理', icon: 'Bell', roles: ['store_admin'], group: '运营进阶' },
];

const visibleMenus = computed(() =>
  menus.filter((m) => !m.roles || hasAny(auth.roles, m.roles)),
);

const groupedMenus = computed(() => {
  const groups = new Map<string, MenuItem[]>();
  for (const item of visibleMenus.value) {
    const key = item.group ?? '其他';
    const arr = groups.get(key) ?? [];
    arr.push(item);
    groups.set(key, arr);
  }
  return Array.from(groups.entries()).map(([title, items]) => ({ title, items }));
});

const activePath = computed(() => route.path);

async function handleLogout(): Promise<void> {
  try {
    await ElMessageBox.confirm('确认退出登录？', '提示', { type: 'warning' });
  } catch {
    return;
  }
  await auth.logout();
  router.push({ name: 'login' });
}

onMounted(() => {
  ctx.start();
});

onBeforeUnmount(() => {
  ctx.stop();
});
</script>

<template>
  <el-container class="layout-root">
    <el-aside width="220px" class="side">
      <div class="brand">
        <div class="brand-name">丝馥生</div>
        <div class="brand-sub">门店管理</div>
      </div>
      <el-menu
        :default-active="activePath"
        router
        background-color="#0f2a24"
        text-color="#dfd6c0"
        active-text-color="#d4b888"
      >
        <template v-for="group in groupedMenus" :key="group.title">
          <div class="menu-group">{{ group.title }}</div>
          <el-menu-item v-for="item in group.items" :key="item.path" :index="item.path">
            <span>{{ item.title }}</span>
          </el-menu-item>
        </template>
      </el-menu>
    </el-aside>
    <el-container>
      <el-header class="topbar">
        <div class="store-info">
          <span class="store-name">{{ auth.storeName || '未关联门店' }}</span>
          <span class="waiting">等待服务客户：<b>{{ ctx.waitingCount }}</b></span>
          <span class="device">在线设备：<b>{{ ctx.onlineDeviceCount }}</b></span>
        </div>
        <div class="user-info">
          <span class="hello">{{ auth.displayName }}</span>
          <el-dropdown>
            <el-button size="small" type="primary" link>账户</el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="handleLogout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>
      <el-main class="main">
        <slot />
      </el-main>
    </el-container>
  </el-container>
</template>

<style scoped>
.layout-root {
  height: 100vh;
}

.side {
  background: #0f2a24;
  color: #dfd6c0;
  height: 100vh;
  overflow-y: auto;
}

.brand {
  padding: 18px 20px 20px;
  border-bottom: 1px solid #1a3a32;
}

.brand-name {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.18em;
  color: #d4b888;
}

.brand-sub {
  font-size: 12px;
  color: #7b938b;
  margin-top: 4px;
}

.menu-group {
  font-size: 11px;
  letter-spacing: 0.18em;
  padding: 12px 20px 6px;
  color: #7b938b;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-bottom: 1px solid #ede7d8;
}

.store-info {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 14px;
}

.store-name {
  font-weight: 600;
  font-size: 16px;
  color: #0f2a24;
}

.waiting b,
.device b {
  color: #b8935a;
  font-size: 16px;
  margin-left: 4px;
  font-variant-numeric: tabular-nums;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.hello {
  font-size: 14px;
  color: #486860;
}

.main {
  background: #fbf8f1;
  padding: 20px 24px;
}
</style>
